import type { ReactNode } from 'react';

type PreviewTab = {
  name: string;
};

type FormPreviewWorkspaceShellProps = {
  tabs: PreviewTab[];
  activeTab: string;
  onTabChange: (tab: string) => void;
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
  children: ReactNode;
};

function classNames(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

export default function FormPreviewWorkspaceShell({
  tabs,
  activeTab,
  onTabChange,
  isExpanded = false,
  onToggleExpanded,
  children,
}: FormPreviewWorkspaceShellProps) {
  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-paper-muted">
      <div className="shrink-0 border-b border-edge bg-white px-3 py-3">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2" aria-hidden="true">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sage opacity-40" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-sage" />
              </span>
              <p className="truncate text-sm font-semibold text-ink">Live preview</p>
            </div>
            <p className="mt-0.5 hidden text-xs text-ink-muted sm:block">
              Updates as you customize the form
            </p>
          </div>

          {onToggleExpanded && (
            <button
              type="button"
              onClick={onToggleExpanded}
              aria-label={isExpanded ? 'Restore preview size' : 'Expand preview'}
              aria-pressed={isExpanded}
              title={isExpanded ? 'Restore preview size' : 'Expand preview'}
              className="shrink-0 rounded-md border border-edge bg-white p-2 text-ink-muted transition-colors hover:bg-paper-inset hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage/40"
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                aria-hidden="true"
              >
                {isExpanded ? (
                  <>
                    <path d="M8 3v5H3M16 3v5h5M8 21v-5H3M16 21v-5h5" />
                    <path d="m3 8 5-5m13 5-5-5M3 16l5 5m13-5-5 5" />
                  </>
                ) : (
                  <>
                    <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" />
                    <path d="m3 3 5 5m13-5-5 5M3 21l5-5m13 5-5-5" />
                  </>
                )}
              </svg>
            </button>
          )}
        </div>

        <nav className="flex w-full rounded-md bg-paper-inset p-0.5" aria-label="Preview mode">
          {tabs.map((tab) => (
            <button
              type="button"
              key={tab.name}
              onClick={() => onTabChange(tab.name)}
              aria-pressed={tab.name === activeTab}
              className={classNames(
                tab.name === activeTab
                  ? 'border border-edge bg-white text-ink shadow-sm'
                  : 'border border-transparent bg-transparent text-ink-secondary hover:text-ink',
                'flex-1 whitespace-nowrap rounded-md px-2 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage/40',
              )}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      <div
        className="min-h-0 w-full flex-1 overflow-auto p-3 sm:p-5"
        style={{
          backgroundColor: '#f3f0e9',
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(107, 100, 88, 0.12) 1px, transparent 0)',
          backgroundSize: '18px 18px',
        }}
      >
        <div
          className={classNames(
            'relative isolate mx-auto min-h-full max-w-full rounded-xl border border-edge bg-white shadow-[0_18px_45px_-30px_rgba(48,43,36,0.55)]',
            activeTab === 'Overlay' ? 'overflow-auto' : 'overflow-hidden',
          )}
          // Keep fixed donor footers and their scoped portals inside this
          // preview's containing block, never above dashboard navigation.
          style={{ transform: 'translateZ(0)' }}
          data-form-preview-viewport
        >
          {children}
        </div>
      </div>
    </div>
  );
}
